import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';
import { scaledPx } from 'containers/Intelligence/theme/scale';

import { formatSharePercent, sharePercent } from '../formatMetric';
import { HoverTooltipSlot } from '../hoverTooltip';

// NOTE (conventions/repo-guidelines/patterns.md § When deviating, document why): this component does
// not wrap antd's `Progress`. The design's row is a label/value line above a pill track whose fill
// carries a per-row tone token, with the percentage exposed only through the track's accessible name
// — antd's `Progress` owns its own `percent` text, stroke colour and inner markup, so matching the
// design through it means overriding more of it than is left. The shared clamp/format still comes
// from `../formatMetric`, so this row cannot disagree with a sibling readout.
// `info` and `accent` are additive, for `589-29081`: the Eligibility signals rows are inked in five
// hues, two of them blue (Total Credit Limit, Affordability) and one purple. No existing caller
// moves — an untoned row still paints in `DEFAULT_FILL`, which is what `accent` names explicitly.
export type ProgressRowTone = 'positive' | 'warning' | 'negative' | 'info' | 'accent';

export interface IProgressRowProps {
  label: string;
  /** Pre-formatted by the caller: a row's value is a rate, a count or a currency amount. */
  value: string;
  /** Level as a 0-1 fraction, the same scale as `RankedBarList.pct`. */
  pct: number;
  tone?: ProgressRowTone;
  /**
   * Draws the track alone, without the label/value line above it, for a bar whose figures the
   * surrounding tile already prints (PLA-9674). `label` then names the track for assistive tech.
   */
  showText?: boolean;
  /** A colour or a CSS gradient for the fill, where a screen paints one outside the tone set. */
  fill?: string;
  /** Track colour, where a screen draws the track in its own ink. */
  trackColor?: string;
  /** Track height and corner radius in drawn px; they follow the responsive scale. */
  thickness?: number;
  radius?: number;
  /** Shown above the end of the fill while the track is hovered or focused. */
  tooltip?: React.ReactNode;
  className?: string;
}

// An untoned row is painted in the same fill as the family's other bars (`RankedBarList`).
export const DEFAULT_FILL = tokens.color['accent-purple'];

// Exported so `SignalMatrix` — the same bar drawn with two value columns beside it rather than one
// above it — cannot drift onto a second copy of this mapping.
export const TONE_FILLS: Record<ProgressRowTone, string> = {
  positive: tokens.color.positive,
  warning: tokens.color.warning,
  negative: tokens.color.negative,
  info: tokens.color['accent-blue'],
  accent: DEFAULT_FILL
};

const Row = styled.div<{ $anchored: boolean }>`
  ${({ $anchored }) => ($anchored ? 'position: relative;' : '')}

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

const TextLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const RowLabel = styled.span`
  min-width: 0;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const RowValue = styled.span`
  flex: 0 0 auto;
  margin-left: ${tokens.space['12']};
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['body-line']};
  white-space: nowrap;
`;

const Track = styled.div<{ $color?: string; $thickness?: number; $radius?: number }>`
  position: relative;
  overflow: hidden;
  height: ${({ $thickness }) => ($thickness === undefined ? '6px' : scaledPx($thickness))};
  border-radius: ${({ $radius }) => ($radius === undefined ? tokens.radius.xs : scaledPx($radius))};
  background: ${({ $color }) => $color || tokens.color['track-bg']};
`;

const Fill = styled.span`
  display: block;
  height: 100%;
  border-radius: inherit;
`;

const ProgressRow: React.FC<IProgressRowProps> = ({
  label,
  value,
  pct,
  tone,
  showText = true,
  fill: fillOverride,
  trackColor,
  thickness,
  radius,
  tooltip,
  className
}) => {
  const [tooltipShown, setTooltipShown] = React.useState(false);
  const hasLevel = Number.isFinite(pct);
  const fill = fillOverride || (tone ? TONE_FILLS[tone] : DEFAULT_FILL);

  return (
    <Row
      $anchored={Boolean(tooltip)}
      className={className}
      data-testid="progress-row"
      data-tone={tone || 'none'}
    >
      {showText ? (
        <TextLine>
          <RowLabel data-testid="progress-row-label">{label}</RowLabel>
          <RowValue data-testid="progress-row-value">{value}</RowValue>
        </TextLine>
      ) : null}
      {/* An unmeasured level draws no track at all: an empty one asserts a measured zero. */}
      {hasLevel ? (
        <Track
          $color={trackColor}
          $thickness={thickness}
          $radius={radius}
          {...(tooltip
            ? {
                tabIndex: 0,
                onMouseEnter: () => setTooltipShown(true),
                onMouseLeave: () => setTooltipShown(false),
                onFocus: () => setTooltipShown(true),
                onBlur: () => setTooltipShown(false)
              }
            : {})}
          // `value` is the row's figure, not its share, so the level is announced nowhere else.
          role="img"
          aria-label={showText ? formatSharePercent(pct) : `${label} ${formatSharePercent(pct)}`}
          data-testid="progress-row-track"
        >
          <Fill
            data-color={fill}
            data-testid="progress-row-fill"
            style={{ width: `${sharePercent(pct)}%`, background: fill }}
          />
        </Track>
      ) : null}
      {tooltip && tooltipShown && hasLevel ? (
        <HoverTooltipSlot
          role="tooltip"
          // Clamped so a box over a near-empty or near-full track stays over the row.
          style={{ left: `${Math.min(Math.max(sharePercent(pct), 15), 85)}%` }}
          data-testid="progress-row-tooltip"
        >
          {tooltip}
        </HoverTooltipSlot>
      ) : null}
    </Row>
  );
};

export default ProgressRow;
